/**
 * How the club writes a golf number down.
 *
 * Five formatters, each of which is a rule about how golf is written rather
 * than a rendering convenience — which is why they live together and are
 * tested rather than inlined per screen.
 *
 * `formatPlayedOn`, `formatStamp` and `formatIndex` were LIFTED verbatim from
 * `ClubhouseScreen.tsx`, which carried its own copies while the clubhouse
 * rewire was in flight; that screen imports them from here now and the
 * duplication is gone.
 */

/** The club's "no answer" — never a zero, never an empty cell. */
const NO_ANSWER = '—'

/**
 * "21 July" from a date-only `played_on`.
 *
 * ⚠️ Split into parts rather than handed to `new Date(iso)`: a bare
 * YYYY-MM-DD parses as UTC MIDNIGHT, which renders as the day before
 * everywhere west of Greenwich. A round played on the 21st reading "20 July"
 * to every member in New York is the whole reason this is not one line.
 */
export function formatPlayedOn(iso: string | null): string {
  if (!iso) return NO_ANSWER
  const [year, month, day] = iso.split('-').map(Number)
  if (!year || !month || !day) return iso
  return new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'long' }).format(
    new Date(year, month - 1, day),
  )
}

/**
 * "11 August" from a full TIMESTAMP — when the club last settled an index, or
 * when a round was posted.
 *
 * Deliberately not `formatPlayedOn`: an ISO instant carries its own zone and
 * `Date` reads it correctly, so the part-splitting above would be wrong here.
 * Returns null rather than a dash — a caller that has nothing to say about a
 * stamp usually wants to omit the line, not print a gap in it.
 */
export function formatStamp(iso: string | null): string | null {
  const date = iso ? new Date(iso) : null
  if (!date || Number.isNaN(date.getTime())) return null
  return new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'long' }).format(date)
}

/**
 * The weekday a round was played — "Saturday" — from a date-only `played_on`.
 *
 * ⚠️ Split into parts for `formatPlayedOn`'s reason, and here the trap BITES
 * HARDER than a date being one day out: a bare YYYY-MM-DD parses as UTC
 * midnight, so everywhere west of Greenwich the weekday names the day BEFORE —
 * and a wrong weekday printed beside a right date is an error nobody can spot.
 *
 * `null` rather than a dash: a caller with no date wants a different sentence
 * ("A round at Kissena Park"), not a gap in this one.
 *
 * `RoundHead` and `ShareHero` both name their day through this, so the member's
 * round page and the public share card cannot disagree about a Saturday. The
 * private English weekday table `RoundHead` used to keep is gone.
 */
export function formatWeekday(iso: string | null): string | null {
  if (!iso) return null
  const [year, month, day] = iso.split('-').map(Number)
  if (!year || !month || !day) return null
  return new Intl.DateTimeFormat(undefined, { weekday: 'long' }).format(new Date(year, month - 1, day))
}

/**
 * A Handicap Index as golf writes it: one decimal, and a PLUS-HANDICAP — an
 * index better than scratch, which the club stores as a negative — rendered
 * "+1.4" rather than "-1.4".
 *
 * `null` is a real answer and not a gap: fewer than the minimum counting
 * rounds is "Unrated", a designed state rather than a loading one.
 */
export function formatIndex(index: number | null): string {
  if (index === null) return 'Unrated'
  return index < 0 ? `+${Math.abs(index).toFixed(1)}` : index.toFixed(1)
}

/**
 * A Score Differential as the register prints it: one decimal, `18.2`, and a
 * differential better than the rating written `−1.2` with a **U+2212 MINUS
 * SIGN** for the same tabular-figures reason as `formatToPar`.
 *
 * Deliberately NOT `formatIndex`: a negative differential is a genuinely
 * negative number (a round played better than the course's rating), not the
 * club's plus-handicap convention, so writing it "+1.2" would invert it. The
 * two look alike on the wire and mean opposite things, which is exactly why
 * they are two functions.
 *
 * `null` is a member with no counting card yet — a dash, never a zero.
 */
export function formatDifferential(differential: number | null | undefined): string {
  if (differential === null || differential === undefined || !Number.isFinite(differential)) {
    return NO_ANSWER
  }
  const written = Math.abs(differential).toFixed(1)
  return differential < 0 ? `−${written}` : written
}

/**
 * A score against par, the way a scoreboard prints it: `E`, `+3`, `−2`.
 *
 * ⚠️ The minus is **U+2212 MINUS SIGN**, not a hyphen. The club's numerals are
 * Fraunces tabular figures, where a hyphen-minus is a short mid-height dash
 * that reads as a dropped character next to a `+` of full sign height — and in
 * a column of to-par cells the two would not even align. Typing `-` here is
 * the mistake this comment exists to prevent.
 *
 * `E` for level, never `0` and never `+0`: nobody says "plus zero".
 *
 * `null` is the club having nothing to compare against — a round with no par
 * for the holes recorded — and is a dash rather than an invented level.
 */
export function formatToPar(toPar: number | null | undefined): string {
  if (toPar === null || toPar === undefined || !Number.isFinite(toPar)) return NO_ANSWER
  if (toPar === 0) return 'E'
  return toPar > 0 ? `+${toPar}` : `−${Math.abs(toPar)}`
}
